import { useEffect, useMemo } from 'react';
import { usePermissions } from '@/contexts/PermissionContext';
import { useUser } from '@/contexts/UserContext';

/**
 * Role-gated content restrictions. Reads the restrict_* keys from the user's
 * organization role (via isRestricted, which fails open) and applies:
 *  - restrict_copy:       body class + capture-phase blocks on copy/cut/contextmenu/dragstart
 *  - restrict_print:      body class (print stylesheet hides content) + Cmd/Ctrl+P swallowed
 *  - restrict_screenshot: tiled attribution watermark overlay
 * Renders nothing and installs nothing when no restriction is active.
 */
const WATERMARK_TILE_W = 360;
const WATERMARK_TILE_H = 240;

const buildWatermarkDataUri = (text) => {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${WATERMARK_TILE_W}" height="${WATERMARK_TILE_H}">` +
    `<text x="50%" y="50%" text-anchor="middle" dominant-baseline="middle" ` +
    `transform="rotate(-30 ${WATERMARK_TILE_W / 2} ${WATERMARK_TILE_H / 2})" ` +
    `font-family="sans-serif" font-size="14" fill="#000" fill-opacity="0.10">${escaped}</text>` +
    `</svg>`;
  return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
};

const preventDefault = (e) => {
  e.preventDefault();
};

export function ContentProtection() {
  const { isRestricted } = usePermissions();
  const { user } = useUser();

  const copyBlocked = isRestricted('restrict_copy');
  const printBlocked = isRestricted('restrict_print');
  const watermarkOn = isRestricted('restrict_screenshot');

  // Body classes drive the CSS in index.css (user-select and the print stylesheet).
  useEffect(() => {
    document.body.classList.toggle('restrict-copy', copyBlocked);
    document.body.classList.toggle('restrict-print', printBlocked);
    return () => {
      document.body.classList.remove('restrict-copy', 'restrict-print');
    };
  }, [copyBlocked, printBlocked]);

  // Capture phase so page-level handlers never see the event.
  useEffect(() => {
    if (!copyBlocked) return undefined;
    const events = ['copy', 'cut', 'contextmenu', 'dragstart'];
    events.forEach((name) => document.addEventListener(name, preventDefault, true));
    return () => {
      events.forEach((name) => document.removeEventListener(name, preventDefault, true));
    };
  }, [copyBlocked]);

  useEffect(() => {
    if (!printBlocked) return undefined;
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && typeof e.key === 'string' && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [printBlocked]);

  const watermarkText = useMemo(() => {
    const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ');
    const date = new Date().toISOString().slice(0, 10);
    return [name, user?.email, date].filter(Boolean).join('  ·  ');
  }, [user?.first_name, user?.last_name, user?.email]);

  const watermarkStyle = useMemo(() => {
    if (!watermarkOn) return null;
    return {
      position: 'fixed',
      inset: 0,
      pointerEvents: 'none',
      zIndex: 2147483000,
      backgroundImage: buildWatermarkDataUri(watermarkText),
      backgroundRepeat: 'repeat',
    };
  }, [watermarkOn, watermarkText]);

  if (!watermarkStyle) return null;
  return <div aria-hidden="true" style={watermarkStyle} />;
}

export default ContentProtection;
